// @ts-nocheck
// TODO: tighten types after migration
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./AdminHome.css";
import "./AdminDatabase.css";
import { useNavPinned } from "./useNavPinned";
import { API_ENDPOINTS } from "../config/api";

function AdminHomeIcon({ size = 22 }) {
    return (
        <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
            <path d="M12 3L4 9v12h5v-7h6v7h5V9z"/>
        </svg>
    );
}

function AdminDatabaseIcon({ size = 22 }) {
    return (
        <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
            <path d="M12 3C7.58 3 4 4.79 4 7s3.58 4 8 4 8-1.79 8-4-3.58-4-8-4zM4 9v3c0 2.21 3.58 4 8 4s8-1.79 8-4V9c0 2.21-3.58 4-8 4s-8-1.79-8-4zm0 5v3c0 2.21 3.58 4 8 4s8-1.79 8-4v-3c0 2.21-3.58 4-8 4s-8-1.79-8-4z"/>
        </svg>
    );
}

// Helper Component for consistent card styling
const DatabaseCard = ({ title, dbKey, loading, error, collections }) => {
    return (
        <div className="admin-card rounded-[12px] border border-slider bg-bg p-[12px] shadow-[0_2px_10px_rgba(0,0,0,0.05)]">
            <div className="admin-database-card-header mb-[8px]">
                <h2 className="admin-database-card-title text-[1rem] font-[700]">{title}</h2>
            </div>
            
            {loading[dbKey] && (
                <div className="admin-database-loading flex items-center gap-[8px] py-[10px] text-[0.92rem] text-[#666]">
                    <div className="spinner-small"></div> {/* Assuming spinner class exists or is simple enough */}
                    Loading...
                </div>
            )}
            
            {error[dbKey] && (
                <div className="admin-database-error rounded-[8px] border border-[#fecaca] bg-[#fff5f5] p-[8px] text-[0.9rem] text-[#b91c1c]">
                    {error[dbKey]}
                </div>
            )}
            
            {!loading[dbKey] && !error[dbKey] && (
                <div className="admin-database-collections flex flex-col gap-[6px]">
                    {collections[dbKey].map((c) => (
                        <Link
                            key={c.name}
                            to={`/admin/database/${dbKey}/${c.name}`}
                            className="admin-collection-item flex items-center justify-between rounded-[8px] border border-slider px-[10px] py-[8px] no-underline transition-colors hover:bg-hover"
                        >
                            <div className="admin-collection-name-group flex items-center gap-[8px]">
                                <span className="admin-collection-arrow">→</span>
                                <span style={{ fontWeight: 500 }}>{c.name}</span>
                            </div>
                            <span className="admin-collection-meta rounded-[999px] bg-hover px-[8px] py-[2px] text-[0.8rem]">{c.count}</span>
                        </Link>
                    ))}
                    {collections[dbKey].length === 0 && (
                        <div className="admin-empty-state rounded-[8px] border border-dashed border-slider p-[10px] text-[0.9rem] text-[#777]">No collections found.</div>
                    )}
                </div>
            )}
        </div>
    );
};

export default function AdminDatabase() {
    const [loading, setLoading] = useState({
        creddb: false,
        blogdb: false,
        newsletterdb: false,
        podcastdb: false,
        storedb: false,
        sponsorshipdb: false,
        userthoughts: false,
        newsroomdb: false,
        learnwithai: false,
        socialmediadb: false,
        affiliateproductsdb: false,
    });
    const [error, setError] = useState({
        creddb: "",
        blogdb: "",
        newsletterdb: "",
        podcastdb: "",
        storedb: "",
        sponsorshipdb: "",
        userthoughts: "",
        newsroomdb: "",
        learnwithai: "",
        socialmediadb: "",
        affiliateproductsdb: "",
    });
    const [collections, setCollections] = useState({
        creddb: [],
        blogdb: [],
        newsletterdb: [],
        podcastdb: [],
        storedb: [],
        sponsorshipdb: [],
        userthoughts: [],
        newsroomdb: [],
        learnwithai: [],
        socialmediadb: [],
        affiliateproductsdb: [],
    });

    const fetchDatabase = async (dbName, endpoint) => {
        setLoading(prev => ({ ...prev, [dbName]: true }));
        setError(prev => ({ ...prev, [dbName]: "" }));
        try {
            const token = sessionStorage.getItem("token");
            
            // Check if token exists
            if (!token || token.trim() === '') {
                setError(prev => ({ ...prev, [dbName]: "Please log in to access the admin panel." }));
                setCollections(prev => ({ ...prev, [dbName]: [] }));
                setLoading(prev => ({ ...prev, [dbName]: false }));
                return;
            }
            
            let res;
            try {
                // Add timeout to fetch request (30 seconds)
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 30000);
                
                try {
                    res = await fetch(`${endpoint}?limit=1`, {
                        headers: {
                            "Content-Type": "application/json",
                            "Accept": "application/json",
                            "Authorization": `Bearer ${token}`
                        },
                        credentials: "include",
                        cache: "no-store",
                        signal: controller.signal
                    });
                    clearTimeout(timeoutId);
                } catch (fetchError) {
                    clearTimeout(timeoutId);
                    
                    // Handle abort (timeout)
                    if (fetchError.name === 'AbortError') {
                        setError(prev => ({ ...prev, [dbName]: "Request timeout: Server took too long to respond. Please try again." }));
                        setCollections(prev => ({ ...prev, [dbName]: [] }));
                        setLoading(prev => ({ ...prev, [dbName]: false }));
                        return;
                    }
                    
                    // Handle network errors (CORS, connection refused, etc.)
                    if (fetchError.message === 'Failed to fetch' || fetchError.name === 'TypeError') {
                        setError(prev => ({ ...prev, [dbName]: "Network error: Unable to connect to server. Please check your connection and ensure the server is running." }));
                        setCollections(prev => ({ ...prev, [dbName]: [] }));
                        setLoading(prev => ({ ...prev, [dbName]: false }));
                        return;
                    }
                    throw fetchError;
                }
            } catch (fetchError) {
                // Handle any other fetch errors
                if (fetchError.message === 'Failed to fetch' || fetchError.name === 'TypeError') {
                    setError(prev => ({ ...prev, [dbName]: "Network error: Unable to connect to server. Please check your connection and ensure the server is running." }));
                    setCollections(prev => ({ ...prev, [dbName]: [] }));
                    setLoading(prev => ({ ...prev, [dbName]: false }));
                    return;
                }
                throw fetchError;
            }
            
            let json;
            if (res.ok) {
                json = await res.json();
                let cols = json && Array.isArray(json.collections) ? json.collections : [];
                
                // Filter collections based on database
                // ImageAssets should only be in BlogDb
                if (dbName !== 'blogdb') {
                    cols = cols.filter(c => c.name !== 'ImageAssets');
                }
                
                // PodcastsAudioDetails and PodcastGuests should only be in PodcastDb
                if (dbName !== 'podcastdb') {
                    cols = cols.filter(c => c.name !== 'PodcastsAudioDetails');
                    cols = cols.filter(c => c.name !== 'PodcastGuests');
                }
                
                setCollections(prev => ({ ...prev, [dbName]: cols }));
            } else {
                let errorMessage = `Failed to load ${dbName} (${res.status})`;
                
                // Clone the response before reading it to avoid "body stream already read" error
                const clonedRes = res.clone();
                
                try {
                    const errorJson = await clonedRes.json();
                    errorMessage = errorJson.message || errorMessage;
                    
                    // If token is invalid/expired or missing, clear it and suggest re-login
                    if (res.status === 401 || res.status === 403) {
                        if (errorJson.message && (errorJson.message.includes('token') || errorJson.message.includes('Token') || errorJson.message.includes('Authorization') || errorJson.message.includes('authorization'))) {
                            sessionStorage.removeItem("token");
                            sessionStorage.removeItem("user");
                            errorMessage = "Session expired. Please log in again.";
                        }
                    }
                } catch (jsonError) {
                    // If JSON parsing fails, try text
                    try {
                        const txt = await res.text();
                        if (txt) {
                            try {
                                const parsed = JSON.parse(txt);
                                errorMessage = parsed.message || errorMessage;
                            } catch {
                                errorMessage = txt || errorMessage;
                            }
                        }
                    } catch (textError) {
                        // If both fail, use default error message
                        console.error(`Error reading response for ${dbName}:`, textError);
                    }
                }
                throw new Error(errorMessage);
            }
        } catch (e) {
            // Handle different types of errors
            let errorMessage = e.message || `Failed to load ${dbName}`;
            
            // Check if it's a network error
            if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError') || errorMessage.includes('Network error')) {
                errorMessage = "Network error: Unable to connect to server. Please check your connection and try again.";
            }
            
            setError(prev => ({ ...prev, [dbName]: errorMessage }));
            setCollections(prev => ({ ...prev, [dbName]: [] }));
        } finally {
            setLoading(prev => ({ ...prev, [dbName]: false }));
        }
    };

    useEffect(() => {
        // Fetch all databases in parallel for faster loading
        Promise.all([
            fetchDatabase('creddb', API_ENDPOINTS.ADMIN.DATABASE_CREDDB),
            fetchDatabase('blogdb', API_ENDPOINTS.ADMIN.DATABASE_BLOGDB),
            fetchDatabase('newsletterdb', API_ENDPOINTS.ADMIN.DATABASE_NEWSLETTERDB),
            fetchDatabase('podcastdb', API_ENDPOINTS.ADMIN.DATABASE_PODCASTDB),
            fetchDatabase('storedb', API_ENDPOINTS.ADMIN.DATABASE_STOREDB),
            fetchDatabase('sponsorshipdb', API_ENDPOINTS.ADMIN.DATABASE_SPONSORSHIPDB),
            fetchDatabase('userthoughts', API_ENDPOINTS.ADMIN.DATABASE_USERTHOUGHTS),
            fetchDatabase('newsroomdb', API_ENDPOINTS.ADMIN.DATABASE_NEWSROOMDB),
            fetchDatabase('learnwithai', API_ENDPOINTS.ADMIN.DATABASE_LEARNWITHAI),
            fetchDatabase('socialmediadb', API_ENDPOINTS.ADMIN.DATABASE_SOCIALMEDIADB),
            fetchDatabase('affiliateproductsdb', API_ENDPOINTS.ADMIN.DATABASE_AFFILIATEPRODUCTSDB),
        ]).catch(err => {
            console.error('Error fetching databases:', err);
        });
    }, []);

    const [navPinned, setNavPinned] = useNavPinned();

    return (
        <div className="admin-container w-full max-w-none px-0 pb-[20px]">
            <nav className={`admin-topnav sticky top-[10px] z-[50] mb-[14px] flex items-center gap-[10px] rounded-[12px] bg-bg/95 p-[8px] shadow-[0_4px_16px_rgba(0,0,0,0.08)] backdrop-blur ${navPinned ? ' admin-topnav-pinned' : ''}`}>
                <Link to="/admin" className="admin-topnav-item flex h-[40px] w-[40px] items-center justify-center rounded-[8px]" title="Home">
                    <AdminHomeIcon />
                </Link>
                <Link to="/admin/database" className="admin-topnav-item admin-topnav-active flex h-[40px] w-[40px] items-center justify-center rounded-[8px]" title="Database">
                    <AdminDatabaseIcon />
                </Link>
                <Link to="/admin/user-dashboard" className="admin-topnav-item flex h-[40px] w-[40px] items-center justify-center rounded-[8px]" title="Dashboard">
                    <i className="fi fi-ss-chart-pie" style={{ fontSize: 20, lineHeight: 1, display: "flex", alignItems: "center" }}></i>
                </Link>
                <button
                    className={`admin-topnav-pin-btn ml-auto flex h-[36px] items-center justify-center rounded-[8px] border border-slider px-[10px]${navPinned ? ' admin-topnav-pin-active' : ''}`}
                    onClick={() => setNavPinned(v => !v)}
                    title={navPinned ? "Unpin nav" : "Pin nav"}
                >
                    <i className="fi fi-sr-thumbtack"></i>
                </button>
            </nav>
            <main className="admin-content">
                <div className="admin-database-container rounded-[14px] bg-bg p-[14px] shadow-[0_2px_12px_rgba(0,0,0,0.06)]">
                    <h1 style={{ fontSize: '24px', fontWeight: '800', marginBottom: '16px', color: '#111827' }}>Database Management</h1>
                    <div className="admin-database-grid grid grid-cols-1 gap-[10px] md:grid-cols-2 xl:grid-cols-3">
                        {[
                            { title: "BlogDb", dbKey: "blogdb" },
                            { title: "CredDb", dbKey: "creddb" },
                            { title: "LearnWithAI", dbKey: "learnwithai" },
                            { title: "NewsLetterDb", dbKey: "newsletterdb" },
                            { title: "NewsRoomDb", dbKey: "newsroomdb" },
                            { title: "PodcastDb", dbKey: "podcastdb" },
                            { title: "SocialMediaDb", dbKey: "socialmediadb" },
                            { title: "SponsorshipDb", dbKey: "sponsorshipdb" },
                            { title: "StoreDb", dbKey: "storedb" },
                            { title: "UserThoughts", dbKey: "userthoughts" },
                            { title: "AffiliateProductsDb", dbKey: "affiliateproductsdb" },
                        ].sort((a, b) => a.title.localeCompare(b.title)).map(db => (
                            <DatabaseCard 
                                key={db.dbKey} 
                                title={db.title} 
                                dbKey={db.dbKey} 
                                loading={loading} 
                                error={error} 
                                collections={collections} 
                            />
                        ))}
                    </div>
                </div>
            </main>
        </div>
    );
}


